/* global runtime */
// Bilagsmatch og autopilotkø for bank.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const getBookedReceiptDuplicateMatch = (receiptId, receipts = safeArray(mergeState(runtime.appState)?.receiptInbox)) => {
  const receipt = safeArray(receipts).find((entry) => String(entry?.id || '') === String(receiptId || ''));
  if (!receipt) return null;
  if (receipt?.status === 'booked' && ['expense', 'other-sale'].includes(receipt?.bookedAs) && receipt?.linkedEntryId) {
    return { receiptId: receipt.id, originalReceiptId: receipt.id, matchedEntryId: receipt.linkedEntryId, matchedEntryType: receipt.bookedAs, bookedAt: receipt.bookedAt || '', label: receipt.bookedAs === 'expense' ? 'Allerede bokført kjøp' : 'Allerede bokført salg' };
  }
  const keys = receiptUploadDuplicateKeys(receipt);
  if (keys.length === 0) return null;
  const original = safeArray(receipts).find((entry) => String(entry?.id || '') !== String(receipt.id || '')
    && entry?.status === 'booked'
    && ['expense', 'other-sale'].includes(entry?.bookedAs)
    && entry?.linkedEntryId
    && receiptUploadDuplicateKeys(entry).some((key) => keys.includes(key)
      && (!key.startsWith('content:') || receiptDatesWithinWindow(receipt.receiptAnalysis?.receiptDate, entry.receiptAnalysis?.receiptDate))));
  return original ? { receiptId: receipt.id, originalReceiptId: original.id, matchedEntryId: original.linkedEntryId, matchedEntryType: original.bookedAs, bookedAt: original.bookedAt || '', label: original.bookedAs === 'expense' ? 'Allerede bokført kjøp' : 'Allerede bokført salg' } : null;
};
const bookedReceiptDuplicateLineMatches = (statement, receipts = safeArray(mergeState(runtime.appState)?.receiptInbox)) => {
  if ((statement?.source || 'business') !== 'business' || window.PlummeBankReconciliationFinish?.bankStatementSettled?.(statement)) return [];
  return safeArray(statement?.lines).filter((line) => !line?.matchedEntryId && !line?.bookedReceiptAutoMatchDismissedAt).map((line) => {
    const receiptId = line?.receiptInboxEntryId || line?.aiSuggestedReceiptInboxEntryId;
    const duplicateMatch = receiptId ? getBookedReceiptDuplicateMatch(receiptId, receipts) : null;
    return duplicateMatch ? { lineId: line.id, receiptId, duplicateMatch } : null;
  }).filter(Boolean);
};
const autoMatchBookedReceiptDuplicates = async (statementId) => {
  const state = mergeState(runtime.appState);
  const statement = safeArray(state?.bankStatements).find((entry) => entry.id === statementId);
  let matched = 0;
  for (const candidate of bookedReceiptDuplicateLineMatches(statement, state?.receiptInbox)) {
    try {
      await matchBankLineToBookedReceiptDuplicate(statementId, candidate.lineId);
      matched += 1;
    } catch (error) {
      // Backend validerer retning, beløp og at originalposten fortsatt finnes.
      // Et utrygt duplikatsignal skal bli stående til kontroll, ikke stoppe resten.
      console.warn('Kunne ikke koble allerede bokført bilag automatisk.', error);
    }
  }
  return matched;
};
const highConfidenceReceiptMatches = (statement, blockedReceiptIds = new Set()) => {
  if ((statement?.source || 'business') !== 'business') return [];
  const claimed = new Set();
  return safeArray(statement?.lines).map((line) => {
    if (line.receiptInboxEntryId || line.attachmentId || line.matchedEntryId) return null;
    if (line.aiSuggestedReceiptMatchConfidence !== 'high') return null;
    const receiptId = String(line.aiSuggestedReceiptInboxEntryId || '');
    // Det samme bilaget kan ikke dokumentere to betalinger.
    if (!receiptId || claimed.has(receiptId) || blockedReceiptIds.has(receiptId)) return null;
    claimed.add(receiptId);
    return { lineId: line.id, receiptId };
  }).filter(Boolean);
};
const attachHighConfidenceReceiptMatches = async (statementId) => {
  const state = mergeState(runtime.appState);
  const statement = safeArray(state?.bankStatements).find((entry) => entry.id === statementId);
  const blockedReceiptIds = new Set(safeArray(state?.receiptInbox)
    .filter((entry) => getBookedReceiptDuplicateMatch(entry.id, state?.receiptInbox)
      || entry?.receiptAnalysis?.status !== 'completed'
      || entry?.receiptAnalysis?.suggestedPostingType !== 'expense')
    .map((entry) => entry.id));
  let attached = 0;
  for (const { lineId, receiptId } of highConfidenceReceiptMatches(statement, blockedReceiptIds)) {
    try {
      await attachReceiptToBankLine(statementId, lineId, receiptId);
      attached += 1;
    } catch (error) {
      // Et bilag som er bokfort eller kastet i mellomtiden er ikke en feil i
      // avstemmingen. Resten av treffene skal fortsatt kobles.
      console.warn('Automatisk bilagskobling feilet for banklinjen.', error);
    }
  }
  return attached;
};

const autopilotAgentEnabled = (key) => {
  const autopilot = mergeState(runtime.appState)?.settings?.autopilot || {};
  return autopilot.enabled !== false && (!autopilot.agents || autopilot.agents[key] !== false);
};

// Bilagstreffene ble tidligere bare beregnet én gang, da bankutskriften ble
// importert. Et bilag som kom inn dagen etter fikk aldri et treff — banklinjen sto
// igjen som «Mangler bilag» selv om kvitteringen laa i innboksen. Kjoringen er
// regelbasert (`rulesOnly`), sa den koster ingen tokens.
// Regelmotoren pa nytt over apne driftsutskrifter, uten AI. Brukes bade nar et
// nytt bilag kommer inn og nar en faktura endres: begge deler kan gjore et treff
// mulig som ikke fantes da utskriften ble importert.
const refreshBankSuggestionRules = async () => {
  if (!runtime.configured) return 0;
  const open = safeArray(mergeState(runtime.appState)?.bankStatements)
    .filter((statement) => (statement?.source || 'business') === 'business')
    .filter((statement) => !window.PlummeBankReconciliationFinish.bankStatementSettled(statement));
  let updated = 0;
  for (const statement of open) {
    try {
      const suggestions = await suggestBankStatementLines(statement.id, { rulesOnly: true });
      updated += safeArray(suggestions).length;
    } catch (error) {
      console.warn('Kunne ikke oppdatere bankforslagene for utskriften.', error);
    }
  }
  return updated;
};

const refreshBankReceiptMatches = async () => {
  if (!runtime.configured) return 0;
  const open = safeArray(mergeState(runtime.appState)?.bankStatements)
    .filter((statement) => (statement?.source || 'business') === 'business')
    .filter((statement) => !window.PlummeBankReconciliationFinish.bankStatementSettled(statement))
    .filter((statement) => safeArray(statement.lines).some((line) => !line.receiptInboxEntryId && !line.attachmentId && !line.matchedEntryId && num(line.amount) < 0));
  let attached = 0;
  for (const statement of open) {
    try {
      await suggestBankStatementLines(statement.id, { rulesOnly: true });
      attached += await attachHighConfidenceReceiptMatches(statement.id);
    } catch (error) {
      console.warn('Kunne ikke oppdatere bilagstreffene for bankutskriften.', error);
    }
  }
  return attached;
};

const autoMatchBankReceipts = async (statementId) => {
  if (!runtime.configured || !autopilotAgentEnabled('bilag')) return null;
  const state = mergeState(runtime.appState);
  const statement = safeArray(state?.bankStatements).find((entry) => entry.id === statementId);
  const hasOutgoingLine = safeArray(statement?.lines).some((line) => !line?.receiptInboxEntryId && !line?.attachmentId && !line?.matchedEntryId && num(line?.amount) < 0);
  const hasAnalyzedReceipt = safeArray(state?.receiptInbox).some((entry) => !['booked', 'trashed'].includes(String(entry?.status || '')) && entry?.receiptAnalysis?.status === 'completed' && num(entry?.receiptAnalysis?.totalGross) > 0 && Boolean(entry?.receiptAnalysis?.receiptDate));
  // No candidate is an ordinary import result; avoid an HTTP call that can only answer 412.
  if (!hasOutgoingLine || !hasAnalyzedReceipt) return null;
  try {
    return await executeAutopilotTask({ taskId: 'bilagsmatch', action: 'match-inbox-to-bank', payload: { statementId } });
  } catch (error) {
    if (/ingen bilag kunne kobles|no receipt/i.test(error?.message || '')) return null;
    throw error;
  }
};

// Analyse og bilagsmatch er ikke bokforing. Tidligere stoppet et saldoavvik mot
// importovergangen hele klargjoringen, og brukeren satt igjen med en utskrift uten
// forslag, uten koblede bilag og uten noe som forklarte hvorfor. Nettopp da trengs
// analysen mest — det er den som viser hva linjene er. Bokforingen er fortsatt
// sperret av `transitionMessage` i UI og av `assertBankStatementTransition` i backend.
const prepareImportedBankStatement = async (statementId, source = 'business') => {
  if (!statementId || source !== 'business' || !runtime.configured) return;
  await suggestBankStatementLines(statementId, { rulesOnly: true });
  await autoMatchBankReceipts(statementId);
  if (autopilotAgentEnabled('bokforing')) await suggestBankStatementLines(statementId);
};

// Sammendraget den planlagte jobben skriver hver morgen. Brukes bare til å vise
// når agentene sist så på regnskapet — oppgavelista avledes fortsatt fra
// app-state, slik at den aldri er eldre enn det brukeren nettopp gjorde.
const getAutopilotSummary = async (businessId = runtime.activeBusinessId) => {
  try {
    await initFirebase();
    if (!runtime.db || !businessId) return null;
    const snapshot = await runtime.db.doc(`businesses/${businessId}/autopilot/summary`).get();
    return snapshot.exists ? snapshot.data() || null : null;
  } catch (error) {
    return null;
  }
};

// Execution-laget for handlingene backend-koen ikke eier: bilagsmatch,
// fakturasending, purring, kontakter, driftsmidler, arkivering og lonnskjoring.
// Klienten sender bare hvilken handling brukeren godkjente — backend avgjør om
// den er lov, utfører den og skriver audit.
const executeAutopilotTask = async ({ taskId, action, payload = {} } = {}) => {
  if (!runtime.configured) throw new Error(t('svc.mustBeSignedIn'));
  const result = await authorizedPost('autopilotExecutionHttp', {
    businessId: runtime.activeBusinessId,
    taskId,
    action,
    payload,
  });
  if (result.state) {
    runtime.appState = mergeAppMutationState(result.state);
    emitRuntime();
    window.dispatchEvent(new Event(BACKEND_EVENTS.inbox));
  }
  return result;
};

// Handlinger som tar tid bestilles i stedet for aa kjores mens brukeren venter.
// Klienten skriver en jobbpost; `autopilotJobWorker` i Functions plukker den opp
// og utforer den samme koden HTTP-endepunktet bruker.
//
// Forskjellen er at jobben overlever fanen: et HTTP-kall doer naar siden lukkes,
// og da kunne to av fem fakturaer vaert sendt uten at noen visste hvilke.
//
// Klienten kan bare BESTILLE. Firestore-reglene tillater en `queued`-post med
// brukerens egen id, og ingenting mer — status og resultat skrives av
// arbeideren, som sjekker tilgangen paa nytt.
const AUTOPILOT_QUEUED_ACTIONS = ['send-unsent-invoices', 'send-payment-reminders'];
const autopilotActionIsQueued = (action) => AUTOPILOT_QUEUED_ACTIONS.includes(String(action || ''));

const queueAutopilotTask = async ({ taskId, action, payload = {} } = {}) => {
  await initFirebase();
  if (!runtime.configured || !runtime.db) throw new Error(t('svc.mustBeSignedIn'));
  const businessId = runtime.activeBusinessId;
  const userId = runtime.user?.uid || '';
  if (!businessId || !userId) throw new Error(t('svc.mustBeSignedIn'));
  const jobRef = runtime.db.collection(`businesses/${businessId}/autopilotJobs`).doc();
  await jobRef.set({
    id: jobRef.id,
    status: 'queued',
    taskId: String(taskId || ''),
    action: String(action || ''),
    payload: payload && typeof payload === 'object' ? payload : {},
    requestedByUserId: userId,
    requestedByEmail: runtime.user?.email || '',
    requestedAt: new Date().toISOString(),
  });
  return { jobId: jobRef.id, queued: true };
};

// Lytter paa jobbene som ikke er ferdige, og paa dem som blir ferdige mens
// siden er aapen. Uten lytteren maatte flaten pollet, og en jobb som ble ferdig
// etter at brukeren kom tilbake ville aldri blitt kvittert ut.
const watchAutopilotJobs = (onChange) => {
  if (!runtime.db || !runtime.activeBusinessId || typeof onChange !== 'function') return () => {};
  return runtime.db.collection(`businesses/${runtime.activeBusinessId}/autopilotJobs`)
    .orderBy('requestedAt', 'desc')
    .limit(20)
    .onSnapshot(
      (snapshot) => onChange(snapshot.docs.map((doc) => ({ id: doc.id, ...(doc.data() || {}) }))),
      (error) => console.warn('[plumme-autopilot] Kunne ikke lytte paa jobber:', error),
    );
};

const AUTOPILOT_WARM_ACTION_LATENCY_BUDGET_MS = 2000;
const autopilotPerformanceSamples = [];
let autopilotTaskQueueCache = { businessId: '', revision: '', loaded: false, tasks: [] };

const recordAutopilotPerformance = ({ action, startedAt, ok }) => {
  const endedAt = window.performance?.now ? window.performance.now() : Date.now();
  const durationMs = Math.max(0, Math.round((endedAt - startedAt) * 10) / 10);
  const sample = {
    action,
    durationMs,
    ok,
    networkCalls: 1,
    budgetMs: AUTOPILOT_WARM_ACTION_LATENCY_BUDGET_MS,
    overBudget: durationMs > AUTOPILOT_WARM_ACTION_LATENCY_BUDGET_MS,
    at: new Date().toISOString(),
  };
  autopilotPerformanceSamples.push(sample);
  if (autopilotPerformanceSamples.length > 50) autopilotPerformanceSamples.shift();
  window.dispatchEvent(new CustomEvent('plumme-performance-measure', { detail: { area: 'autopilot', ...sample } }));
  return sample;
};

const timedAutopilotPost = async (action, body) => {
  const startedAt = window.performance?.now ? window.performance.now() : Date.now();
  try {
    const payload = await authorizedPost('autopilotTaskQueueHttp', body);
    recordAutopilotPerformance({ action, startedAt, ok: true });
    return payload;
  } catch (error) {
    recordAutopilotPerformance({ action, startedAt, ok: false });
    throw error;
  }
};

const applyAutopilotStateDelta = (delta) => {
  if (!runtime.appState || !delta || typeof delta !== 'object') return false;
  const next = { ...runtime.appState, ...(delta.changed || {}) };
  safeArray(delta.removed).forEach((key) => { delete next[key]; });
  runtime.appState = mergeState(next);
  return true;
};

const applyAutopilotTaskDelta = (delta) => {
  if (!autopilotTaskQueueCache.loaded || !delta || typeof delta !== 'object') return false;
  const removeIds = new Set(safeArray(delta.removeIds).map((id) => String(id || '')));
  const byId = new Map(autopilotTaskQueueCache.tasks
    .filter((task) => !removeIds.has(String(task?.id || '')))
    .map((task) => [String(task?.id || ''), task]));
  safeArray(delta.upsert).forEach((task) => {
    const id = String(task?.id || '');
    if (id) byId.set(id, task);
  });
  const ordered = safeArray(delta.orderIds).map((id) => byId.get(String(id || ''))).filter(Boolean);
  autopilotTaskQueueCache.tasks = ordered.length === byId.size ? ordered : [...byId.values()];
  return true;
};

const getAutopilotTaskQueue = async ({ preferCache = false } = {}) => {
  if (!runtime.activeBusinessId) return [];
  if (preferCache
    && autopilotTaskQueueCache.loaded
    && autopilotTaskQueueCache.businessId === runtime.activeBusinessId
    && autopilotTaskQueueCache.revision === runtime.appStateRevision) {
    return autopilotTaskQueueCache.tasks;
  }
  const requestedRevision = runtime.appStateRevision;
  const payload = await timedAutopilotPost('list', {
    businessId: runtime.activeBusinessId,
    action: 'list',
  });
  autopilotTaskQueueCache = {
    businessId: runtime.activeBusinessId,
    revision: requestedRevision,
    loaded: true,
    tasks: safeArray(payload?.tasks),
  };
  return autopilotTaskQueueCache.tasks;
};

const mutateAutopilotTaskQueue = async (action, input = {}) => {
  if (!runtime.activeBusinessId) return { approved: [], executed: [], tasks: [] };
  const responseMode = runtime.appState ? 'delta' : 'full';
  const payload = await timedAutopilotPost(action, {
    businessId: runtime.activeBusinessId,
    action,
    responseMode,
    ...input,
  });
  const stateApplied = payload?.stateDelta
    ? applyAutopilotStateDelta(payload.stateDelta)
    : Boolean(payload?.state && (runtime.appState = mergeAppMutationState(payload.state)));
  if (payload?.taskDelta) applyAutopilotTaskDelta(payload.taskDelta);
  else if (Array.isArray(payload?.tasks)) {
    autopilotTaskQueueCache = { businessId: runtime.activeBusinessId, revision: String(payload?.revision || runtime.appStateRevision || ''), loaded: true, tasks: payload.tasks };
  }
  if (stateApplied) {
    runtime.appStateRevision = String(payload?.revision || runtime.appStateRevision || '');
    if (autopilotTaskQueueCache.loaded) autopilotTaskQueueCache.revision = runtime.appStateRevision;
    emitRuntime();
    window.dispatchEvent(new Event(BACKEND_EVENTS.inbox));
  }
  return {
    approved: safeArray(payload?.approved),
    executed: safeArray(payload?.executed),
    reopened: safeArray(payload?.reopened),
    task: payload?.task || null,
    tasks: autopilotTaskQueueCache.loaded ? autopilotTaskQueueCache.tasks : safeArray(payload?.tasks),
  };
};

const approveAutopilotTaskQueue = async (taskIds = []) => {
  const ids = [...new Set(safeArray(taskIds).map((id) => String(id || '').trim()).filter(Boolean))];
  if (!ids.length) return { approved: [], tasks: [] };
  return mutateAutopilotTaskQueue('approve', { taskIds: ids });
};

const reopenAutopilotTaskQueue = async (taskId) => {
  const id = String(taskId || '').trim();
  if (!id) throw new Error(t('r1e.autopilot.taskMissing'));
  return mutateAutopilotTaskQueue('reopen', { taskId: id });
};

const bookApprovedAutopilotTasks = async (taskIds = []) => {
  const ids = [...new Set(safeArray(taskIds).map((id) => String(id || '').trim()).filter(Boolean))];
  if (!ids.length) return { executed: [], tasks: [] };
  return mutateAutopilotTaskQueue('execute', { taskIds: ids });
};

const approveAndBookAutopilotTasks = async ({ readyTaskIds = [], approvedTaskIds = [], bankLineUpdates = [], proposalUpdates = [] } = {}) => {
  const readyIds = [...new Set(safeArray(readyTaskIds).map((id) => String(id || '').trim()).filter(Boolean))];
  const approvedIds = [...new Set(safeArray(approvedTaskIds).map((id) => String(id || '').trim()).filter(Boolean))];
  if (!readyIds.length && !approvedIds.length) return { approved: [], executed: [], tasks: [] };
  try {
    return await mutateAutopilotTaskQueue('approve_execute', { readyTaskIds: readyIds, approvedTaskIds: approvedIds, bankLineUpdates, proposalUpdates });
  } catch (error) {
    // Deploy-safe fallback mens en ny klient kan treffe en gammel Function.
    if (!/unsupported autopilot task action/i.test(String(error?.message || ''))) throw error;
    for (const update of safeArray(bankLineUpdates)) {
      await updateBankStatementLine(update.statementId, update.lineId, update.patch);
    }
    for (const update of safeArray(proposalUpdates)) {
      await updateAutopilotTaskProposal(update.taskId, update.proposal);
    }
    const approvedResult = readyIds.length ? await approveAutopilotTaskQueue(readyIds) : { approved: [] };
    const executionIds = [...new Set([...safeArray(approvedResult.approved), ...approvedIds])];
    return bookApprovedAutopilotTasks(executionIds);
  }
};

const getAutopilotPerformanceSnapshot = () => ({
  budgetMs: AUTOPILOT_WARM_ACTION_LATENCY_BUDGET_MS,
  samples: autopilotPerformanceSamples.map((sample) => ({ ...sample })),
});

const queueReceiptDraftCorrectionLearning = (input = {}) => {
  const sourceId = String(input?.entry?.id || '');
  queueAiLearningLog(() => logReceiptDraftCorrection(input), { sourceType: 'receipt_inbox', sourceId, outcome: 'draft_correction' });
};

const updateAutopilotTaskProposal = async (taskId, proposal = {}) => {
  const id = String(taskId || '').trim();
  if (!id) throw new Error(t('r1e.autopilot.taskMissing'));
  return mutateAutopilotTaskQueue('update', { taskId: id, proposal });
};
